import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from '../src/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Loader2, Save, User, Mail, Shield } from 'lucide-react';
import { Skeleton } from './Skeleton';

export const UserSettings: React.FC = () => {
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ displayName: '', email: '' });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!auth.currentUser) return;
      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserProfile(data);
          setFormData({ displayName: data.displayName || '', email: auth.currentUser.email || '' });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setSaving(true);
    setSuccess(false);
    setError('');
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        displayName: formData.displayName
      });
      setUserProfile({ ...userProfile, displayName: formData.displayName });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-xl">
      <h2 className="text-2xl font-bold mb-6 text-white">User Settings</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-xs font-mono text-gray-500 uppercase tracking-widest mb-2">Display Name</label>
          <div className="relative">
            <User className="absolute left-3 top-3 text-gray-500" size={18} />
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-10 py-2.5 text-white focus:outline-none focus:border-green-500 transition-all"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-mono text-gray-500 uppercase tracking-widest mb-2">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-500" size={18} />
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full bg-black/20 border border-white/5 rounded-lg px-10 py-2.5 text-gray-500 cursor-not-allowed"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-mono text-gray-500 uppercase tracking-widest mb-2">Role</label>
          <div className="relative">
            <Shield className="absolute left-3 top-3 text-gray-500" size={18} />
            <input
              type="text"
              value={userProfile?.role || 'User'}
              disabled
              className="w-full bg-black/20 border border-white/5 rounded-lg px-10 py-2.5 text-gray-500 cursor-not-allowed"
            />
          </div>
        </div>

        <AnimatePresence>
          {success && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm text-center"
            >
              Profile updated successfully!
            </motion.div>
          )}
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-500 transition-all flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          Save Changes
        </button>
      </div>
    </div>
  );
};
